import type { HTMLAttributes, InputHTMLAttributes, ReactNode } from 'react'
import React from 'react'
import classNames from 'classnames'

import { WarningIcon } from '@slite/icons'

import { TooltipOnHover } from '../TooltipOnHover/TooltipOnHover'
import type { InputSize } from './constants'
import { inputSizesValues } from './constants'

const DISABLED_CLASSNAMES = 'background-alpha-shade-55'

export type InputProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'size' | 'className'
> & {
  renderLeft?: () => ReactNode
  renderRight?: () => ReactNode
  error?: string | null
  hasError?: boolean
  wrapperClassName?: string
  wrapperStyle?: HTMLAttributes<HTMLDivElement>['style']
  inputClassName?: string
  size?: InputSize
} & { ref?: React.Ref<HTMLInputElement> }

export function Input({
  inputClassName,
  error,
  hasError,
  renderLeft,
  renderRight,
  wrapperClassName,
  wrapperStyle,
  style,
  size = 'medium',
  autoComplete = 'off',
  ref,
  ...props
}: InputProps) {
  const _inputClassName = classNames(
    [
      'background-transparent',
      'font-size-normal shade-00',
      'flexbox-grow border-none',
      'radius-large placeholder-shade-20',
    ],
    {
      'cursor-forbidden': props.disabled,
      'font-size-normal': size !== 'large',
      'font-size-large': size === 'large',
      'padding-horizontal-12': !inputClassName?.includes('padding-'),
    },
    inputClassName
  )

  const _wrapperClassName = classNames(
    //TODO - remove margin from the components, external style should be delegated to external component
    ['border-small radius-large font-family-system'],
    {
      'border-shade-40 ': !(hasError || error),
      'border-red': hasError || error,
    },
    props.disabled && DISABLED_CLASSNAMES,
    !props.disabled && 'background-transparent',
    wrapperClassName
  )

  return (
    <label
      className={classNames(
        'position-relative flexbox flexbox-align-center',
        _wrapperClassName
      )}
      style={wrapperStyle}
    >
      {renderLeft && (
        <div className="flexbox flexbox-align-center">{renderLeft()}</div>
      )}
      <input
        style={{ minHeight: inputSizesValues[size], ...style }}
        {...props}
        autoComplete={autoComplete}
        className={_inputClassName}
        ref={ref}
      />
      {renderRight && (
        <div className="flexbox flexbox-align-center margin-right-4">
          {renderRight()}
        </div>
      )}
      {(hasError || error) &&
        (error ? (
          <TooltipOnHover
            openDelay={200}
            allowContentHover
            positions={['top', 'bottom', 'left', 'right']}
            content={error}
            target={({ ref, props }) => (
              <WarningIcon
                ref={ref}
                {...props}
                size={16}
                className="red margin-right-16"
              />
            )}
          />
        ) : (
          <WarningIcon size={16} className="red margin-right-16" />
        ))}
    </label>
  )
}
